'use client';

import { api } from '@/lib/api';
import { useInfiniteQuery } from '@tanstack/react-query';
import { JobsResponse } from '../types';

export interface FetchJobsParams {
  page: number;
  perPage?: number;
  onlyCurrentAccount?: boolean;
  displayAll?: boolean;
  query?: string;
  accountId?: string;
}

export interface UseJobsOptions {
  enableRefresh?: boolean;
}

const fetchJobs = async ({
  page,
  perPage = 25,
  onlyCurrentAccount,
  displayAll,
  query,
  accountId,
}: FetchJobsParams): Promise<JobsResponse> => {
  const params = new URLSearchParams({
    page: page.toString(),
    perPage: perPage.toString(),
    ...(accountId && { accountId }),
    ...(onlyCurrentAccount && { onlyCurrentAccount: 'true' }),
    ...(displayAll && { displayAll: 'true' }),
    ...(query && { query }),
  });

  const response = await api.get<JobsResponse>(`/p2prealstats.json?${params.toString()}`);
  return response.data;
};

export const useJobs = (
  params: Omit<FetchJobsParams, 'page'>,
  options: UseJobsOptions = {}
) => {
  const { enableRefresh = true } = options;

  return useInfiniteQuery<JobsResponse, Error>({
    queryKey: ['jobs', params],
    queryFn: ({ pageParam }) => fetchJobs({ ...params, page: pageParam as number }),
    getNextPageParam: (lastPage: JobsResponse) => {
      if (lastPage.pagination.page < lastPage.pagination.totalPages) {
        return lastPage.pagination.page + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    refetchInterval: enableRefresh ? 10000 : false,
    staleTime: 0, // Always refetch for polling
  });
};
